import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, FileEdit, Filter, Users, Printer, FileText, FileSpreadsheet } from 'lucide-react';

const TRIMESTRES = ['1º Trimestre', '2º Trimestre', '3º Trimestre'];
const TIPOS = ['APA', 'Trabalho', 'Prontidão', 'Avaliação Trimestral'];

export default function AlunosSemNotaDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, is_admin: false });
  const [year] = useState(new Date().getFullYear());
  const [trimestre, setTrimestre] = useState('1º Trimestre');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [turmaFilter, setTurmaFilter] = useState('all');
  const [discFilter, setDiscFilter] = useState('all');
  const [q, setQ] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { year, trimestre };
      if (q) params.q = q;
      if (tipoFilter !== 'all') params.tipo_avaliacao = tipoFilter;
      if (turmaFilter !== 'all') params.class_group_id = turmaFilter;
      if (discFilter !== 'all') params.subject_id = discFilter;
      const r = await api.get('/equipe/alunos-sem-nota', { params });
      // Normaliza múltiplos formatos de payload: r.data | r.data.items | r.data.data | r.data.results
      const payload = r?.data ?? {};
      const items =
        (Array.isArray(payload) && payload) ||
        payload.items ||
        payload.data ||
        payload.results ||
        [];
      setData({
        items: Array.isArray(items) ? items : [],
        total: payload.total ?? (Array.isArray(items) ? items.length : 0),
        is_admin: !!payload.is_admin,
        tipos_obrigatorios: payload.tipos_obrigatorios || TIPOS,
        warning: payload.warning || null,
      });
    } catch (e) {
      // Cancelado pelo cleanup do useEffect → não é erro real
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.message === 'canceled') {
        return;
      }
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Falha ao carregar alunos sem nota${detail ? ': ' + detail : ''}`);
      setData({ items: [], total: 0, is_admin: false });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trimestre, tipoFilter, turmaFilter, discFilter]);

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchData(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const turmasUnicas = useMemo(() => {
    const m = new Map();
    (data.items || []).forEach(i => m.set(i.class_group_id, i.class_group_name));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [data.items]);

  const discUnicas = useMemo(() => {
    const m = new Map();
    (data.items || []).forEach(i => {
      if (turmaFilter === 'all' || i.class_group_id === turmaFilter) m.set(i.subject_id, i.subject_name);
    });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [data.items, turmaFilter]);

  const handleLancarNota = (item) => {
    onOpenChange(false);
    // Deep-link para o TeacherPortal — abre fluxo oficial (handleSelectClassGroup + handleSelectSubject)
    navigate(`/v2/academico/portal-professor?class_group_id=${item.class_group_id}&subject_id=${item.subject_id}`);
  };

  const [exporting, setExporting] = useState(null); // 'pdf' | 'excel' | null

  const buildExportParams = () => {
    const params = { year, trimestre };
    if (q) params.q = q;
    if (tipoFilter !== 'all') params.tipo_avaliacao = tipoFilter;
    if (turmaFilter !== 'all') params.class_group_id = turmaFilter;
    if (discFilter !== 'all') params.subject_id = discFilter;
    return params;
  };

  const handleExport = async (formato) => {
    setExporting(formato);
    try {
      const r = await api.get(`/equipe/alunos-sem-nota/export/${formato}`, {
        params: buildExportParams(),
        responseType: 'blob',
      });
      // Extrai nome do arquivo do Content-Disposition; fallback genérico
      const cd = r.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fallback = `alunos_sem_nota_${today}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      const fname = m ? m[1] : fallback;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Arquivo ${formato.toUpperCase()} gerado`);
    } catch (e) {
      toast.error(`Falha ao exportar ${formato.toUpperCase()}: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setExporting(null);
    }
  };

  const handlePrint = () => {
    // Window.print() do navegador; a página DialogContent já é estilizada
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden p-0" data-testid="alunos-sem-nota-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-purple-800">
            <Users className="w-5 h-5" /> Alunos sem Nota
            {!loading && (
              <Badge variant="outline" className="ml-2 border-purple-300 text-purple-700">{data.total} pendência{data.total === 1 ? '' : 's'}</Badge>
            )}
            <div className="ml-auto flex items-center gap-1.5 print:hidden">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={handlePrint}
                data-testid="asn-print"
              >
                <Printer className="w-4 h-4 mr-1" /> Imprimir
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={() => handleExport('pdf')}
                disabled={exporting === 'pdf'}
                data-testid="asn-export-pdf"
              >
                {exporting === 'pdf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={() => handleExport('excel')}
                disabled={exporting === 'excel'}
                data-testid="asn-export-excel"
              >
                {exporting === 'excel' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                Excel
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pendências por tipo obrigatório (APA · Trabalho · Prontidão · Avaliação Trimestral). Filtros respeitam suas turmas vinculadas em <b>teacher_assignments</b>.
          </DialogDescription>
        </DialogHeader>

        {/* Warning amarelo (professor sem vínculo etc.) */}
        {data.warning && (
          <div className="mx-6 mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-xs px-3 py-2" data-testid="asn-warning">
            ⚠ {data.warning}
          </div>
        )}

        {/* Filtros */}
        <div className="px-6 py-3 bg-slate-50 border-b grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluno…" className="pl-8 h-9" data-testid="asn-search" />
          </div>
          <Select value={trimestre} onValueChange={setTrimestre}>
            <SelectTrigger className="h-9" data-testid="asn-trimestre"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRIMESTRES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tipoFilter} onValueChange={setTipoFilter}>
            <SelectTrigger className="h-9" data-testid="asn-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={turmaFilter} onValueChange={(v) => { setTurmaFilter(v); setDiscFilter('all'); }}>
            <SelectTrigger className="h-9" data-testid="asn-turma"><SelectValue placeholder="Turma" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as turmas</SelectItem>
              {turmasUnicas.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={discFilter} onValueChange={setDiscFilter}>
            <SelectTrigger className="h-9" data-testid="asn-disciplina"><SelectValue placeholder="Disciplina" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as disciplinas</SelectItem>
              {discUnicas.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabela */}
        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Carregando…</div>
          ) : (data.items || []).length === 0 ? (
            <div className="p-10 text-center text-emerald-600">
              <Filter className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <div className="font-semibold">Tudo em dia!</div>
              <div className="text-xs text-slate-500 mt-1">Nenhuma pendência para os filtros aplicados.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr className="text-[11px] uppercase tracking-wider text-slate-600">
                  <th className="text-left px-3 py-2">Aluno</th>
                  <th className="text-left px-3 py-2">Turma</th>
                  <th className="text-left px-3 py-2">Disciplina</th>
                  <th className="text-left px-3 py-2">Tipo</th>
                  <th className="text-left px-3 py-2">Trimestre</th>
                  <th className="text-left px-3 py-2">Professor</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Ação</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((i, idx) => (
                  <tr key={`${i.student_id}-${i.subject_id}-${i.tipo_avaliacao}-${idx}`} className={idx % 2 ? 'bg-slate-50/40' : 'bg-white'} data-testid={`asn-row-${idx}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{i.student_name}</td>
                    <td className="px-3 py-2 text-slate-700">{i.class_group_name}</td>
                    <td className="px-3 py-2 text-slate-700">{i.subject_name}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{i.tipo_avaliacao}</Badge></td>
                    <td className="px-3 py-2 text-slate-600">{i.trimestre}</td>
                    <td className="px-3 py-2 text-slate-600">{i.professor_nome}</td>
                    <td className="px-3 py-2">
                      <Badge className={i.status === 'Sem lançamento' ? 'bg-rose-100 text-rose-700 hover:bg-rose-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}>
                        {i.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => handleLancarNota(i)} className="h-7 text-[11px] border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`asn-launch-${idx}`}>
                        <FileEdit className="w-3 h-3 mr-1" />Lançar nota
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
